import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import LanguageToggle from "@/components/LanguageToggle";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import NoIndex from "@/components/NoIndex";

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const bn = language === "bn";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Supabase automatically processes the recovery token from the URL hash
    // and fires an AUTH_STATE_CHANGE with event "PASSWORD_RECOVERY"
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setSessionReady(true);
      }
    });

    // Also check if user is already in a recovery session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError(bn ? "পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে।" : "Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError(bn ? "পাসওয়ার্ড মিলছে না।" : "Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(bn ? "পাসওয়ার্ড আপডেট ব্যর্থ হয়েছে।" : error.message);
      } else {
        setSuccess(true);
        setTimeout(() => navigate("/login"), 2500);
      }
    } catch {
      setError(bn ? "কিছু একটা সমস্যা হয়েছে।" : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <><NoIndex /><div className="min-h-screen gradient-bg flex flex-col">
      <div className="gradient-primary px-5 pt-10 pb-16 rounded-b-[2.5rem] relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary-foreground/10" />
        <div className="absolute bottom-4 -left-8 w-28 h-28 rounded-full bg-primary-foreground/5" />
        <div className="flex justify-end mb-6">
          <LanguageToggle />
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center">
          <img src="/logo.png" alt="Sebaco" className="h-16 w-16 rounded-xl object-contain mb-3" />
          <h1 className="text-primary-foreground text-3xl font-extrabold leading-tight">
            {bn ? "নতুন পাসওয়ার্ড" : "New Password"}
          </h1>
          <p className="text-primary-foreground/80 text-sm mt-1">
            {bn ? "আপনার নতুন পাসওয়ার্ড সেট করুন" : "Set your new password below"}
          </p>
        </motion.div>
      </div>

      <motion.div className="flex-1 px-5 -mt-8" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
        <div className="glass rounded-2xl p-6 shadow-float">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <p className="font-bold text-foreground">
                {bn ? "পাসওয়ার্ড সফলভাবে পরিবর্তন হয়েছে!" : "Password updated successfully!"}
              </p>
              <p className="text-xs text-muted-foreground">
                {bn ? "লগইন পেজে নিয়ে যাওয়া হচ্ছে..." : "Redirecting you to login..."}
              </p>
            </div>
          ) : !sessionReady ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">
                {bn ? "যাচাই করা হচ্ছে..." : "Verifying your reset link..."}
              </p>
              <p className="text-[11px] text-muted-foreground/70">
                {bn ? "যদি কিছু না হয়, আবার রিসেট লিংক চেষ্টা করুন।" : "If nothing happens, try the reset link from your email again."}
              </p>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-muted-foreground mb-1.5 block">
                  {bn ? "নতুন পাসওয়ার্ড" : "New Password"}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-12 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-muted-foreground mb-1.5 block">
                  {bn ? "পাসওয়ার্ড নিশ্চিত করুন" : "Confirm Password"}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-12 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground"
                    required
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showConfirm ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2.5">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.97 }}
                className="w-full gradient-primary text-primary-foreground py-3.5 rounded-xl font-bold text-sm shadow-glow flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {bn ? "পাসওয়ার্ড আপডেট করুন" : "Update Password"}
              </motion.button>
            </form>
          )}
        </div>
      </motion.div>
    </div></>
  );
};

export default ResetPasswordPage;
